class DeliveryDateChecker extends HTMLElement {
  connectedCallback() {
    this.fromPincode = this.dataset.fromPincode || '';
    this.sameCityMin = parseInt(this.dataset.sameCityMin) || 1;
    this.sameCityMax = parseInt(this.dataset.sameCityMax) || 2;
    this.sameStateMin = parseInt(this.dataset.sameStateMin) || 3;
    this.sameStateMax = parseInt(this.dataset.sameStateMax) || 5;
    this.nationalMin = parseInt(this.dataset.nationalMin) || 5;
    this.nationalMax = parseInt(this.dataset.nationalMax) || 7;
    this.labelText = this.dataset.label || 'Get it by';

    this.input = this.querySelector('.delivery-checker__input');
    this.btn = this.querySelector('.delivery-checker__btn');
    this.result = this.querySelector('.delivery-checker__result');
    this.resultIcon = this.querySelector('.delivery-checker__result-icon');
    this.resultContent = this.querySelector('.delivery-checker__result-content');

    this.btn.addEventListener('click', () => this.check());
    this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.check(); });
    this.input.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      this.hideResult();
    });
  }

  async check() {
    const pincode = this.input.value.trim();
    if (pincode.length !== 6) {
      this.showError('Enter a valid 6-digit pincode.');
      return;
    }
    this.showLoading();
    try {
      const [toData, fromData] = await Promise.all([
        this.fetchPincode(pincode),
        this.fromPincode ? this.fetchPincode(this.fromPincode) : Promise.resolve(null),
      ]);
      if (!toData) { this.showError('Invalid pincode. Please try again.'); return; }

      const { min, max } = this.getEtaDays(fromData, toData);
      const dateRange = this.getDeliveryDateRange(min, max);
      const location = toData.district ? `, ${toData.district}` : '';
      this.showSuccess(`${dateRange}${location}`);
    } catch {
      this.showError('Unable to check. Please try again.');
    }
  }

  async fetchPincode(pincode) {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    const json = await res.json();
    if (!json || json[0]?.Status !== 'Success') return null;
    const po = json[0].PostOffice[0];
    return { district: po.District, state: po.State };
  }

  getEtaDays(from, to) {
    if (!from) return { min: this.nationalMin, max: this.nationalMax };
    if (from.district === to.district) return { min: this.sameCityMin, max: this.sameCityMax };
    if (from.state === to.state) return { min: this.sameStateMin, max: this.sameStateMax };
    return { min: this.nationalMin, max: this.nationalMax };
  }

  getDeliveryDateRange(minDays, maxDays) {
    const addWorkingDays = (date, days) => {
      const d = new Date(date);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) added++;
      }
      return d;
    };
    const fmt = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    const today = new Date();
    if (minDays === maxDays) return fmt(addWorkingDays(today, minDays));
    return `${fmt(addWorkingDays(today, minDays))} – ${fmt(addWorkingDays(today, maxDays))}`;
  }

  showLoading() {
    this.result.className = 'delivery-checker__result is-visible delivery-checker__result--loading';
    this.resultIcon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>`;
    this.resultContent.innerHTML = '<span class="delivery-checker__result-label">Checking…</span>';
  }

  showSuccess(eta) {
    this.result.className = 'delivery-checker__result is-visible delivery-checker__result--success';
    this.resultIcon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14"><path d="M20 6L9 17l-5-5"/></svg>`;
    this.resultContent.innerHTML = `<span class="delivery-checker__result-label">${this.labelText}&nbsp;</span><span class="delivery-checker__result-eta">${eta}</span>`;
  }

  showError(msg) {
    this.result.className = 'delivery-checker__result is-visible delivery-checker__result--error';
    this.resultIcon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>`;
    this.resultContent.innerHTML = `<span class="delivery-checker__result-error-text">${msg}</span>`;
  }

  hideResult() {
    this.result.classList.remove('is-visible');
  }
}

customElements.define('delivery-date-checker', DeliveryDateChecker);
